import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import useRealtimeNotifications from "../hooks/useRealtimeNotifications";
import Navbar from "../components/Navbar";
import Avatar from "../components/Avatar";
import Linkify from "../components/Linkify";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Plus, X, Trash2, Clock, HelpCircle, HandHelping, HeartHandshake, BookOpen,
  Paperclip, MessageCircle, AlertCircle, Newspaper, Users as UsersIcon, Star,
  ChevronRight, ImageIcon, Layers, PlayCircle, Crown,
  Calendar, Pencil,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20 MB

const CATEGORIES = [
  { key: "Frage",       label: "Frage",       icon: HelpCircle,     hex: "#00e5ff" }, // Cyan
  { key: "Suche Hilfe", label: "Suche Hilfe", icon: HandHelping,    hex: "#facc15" }, // Yellow
  { key: "Biete Hilfe", label: "Biete Hilfe", icon: HeartHandshake, hex: "#4ade80" }, // Green
  { key: "Wissen",      label: "Wissen",      icon: BookOpen,       hex: "#c084fc" }, // Violet
  { key: "Event",       label: "Event",       icon: Calendar,       hex: "#f97316" }, // Orange
];

const getCatStyle = (cat) =>
  CATEGORIES.find((c) => c.key === cat) || { hex: "#64748b", icon: Layers, label: cat || "Sonstiges" };

const BUDGET_OPTIONS = [
  { key: "kein-budget",     label: "Kein Budget" },
  { key: "kleines-budget",  label: "Klein (<500€)" },
  { key: "mittleres-budget",label: "Mittel (500-2.5k€)" },
  { key: "grosses-budget",  label: "Groß (>2.5k€)" },
];

function daysRemaining(iso) {
  const d = new Date(iso) - new Date();
  return d <= 0 ? 0 : Math.ceil(d / (1000 * 60 * 60 * 24));
}
function formatDate(iso) {
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
}

/* ===================================================================== */
/* Widget row — left: members marquee + news / middle: tools / right: videos */
/* ===================================================================== */
function WidgetRow() {
  const [data, setData] = useState({ latest_members: [], top_tools: [], news: [], videos: [] });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/dashboard/widgets`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(() => { /* silent */ })
      .finally(() => setLoading(false));
  }, []);

  const widgetClass = "bg-gradient-to-br from-white/[0.02] to-transparent border border-white/[0.07]";

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10" data-testid="board-widgets">
      {/* ============= LEFT: Members marquee + News ============= */}
      <div className={`${widgetClass} overflow-hidden flex flex-col`}>
        <MembersMarquee members={data.latest_members} loading={loading} onSeeAll={() => navigate("/members")} />
        <div className="flex-1 relative">
          <NewsRotator items={data.news || []} loading={loading} navigate={navigate} />
        </div>
      </div>

      {/* ============= MIDDLE: Top tools ============= */}
      <div className={widgetClass + " p-5"}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Star size={14} className="text-[#00e5ff]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#00e5ff]/80 font-outfit font-semibold">
              Top AI-Tools
            </span>
          </div>
          <button onClick={() => navigate("/tools")} className="text-white/40 hover:text-[#00e5ff] transition-colors" aria-label="Alle Tools">
            <ChevronRight size={14} />
          </button>
        </div>
        {loading ? (
          <div className="h-20 animate-pulse bg-white/[0.02]" />
        ) : data.top_tools.length === 0 ? (
          <p className="text-xs text-white/35">Noch keine bewerteten Tools</p>
        ) : (
          <ul className="space-y-2">
            {data.top_tools.slice(0, 4).map((t) => (
              <li key={t.id} className="flex items-center gap-3 cursor-pointer hover:bg-white/[0.02] -mx-2 px-2 py-1 transition-colors"
                  onClick={() => navigate("/tools")}>
                <div className="w-8 h-8 bg-white flex items-center justify-center p-1 shrink-0">
                  <img src={t.logo_url} alt={t.name} className="max-w-full max-h-full object-contain"
                       onError={(e) => { e.target.src = t.favicon_url || ""; }} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-outfit font-semibold text-white truncate">{t.name}</p>
                  <div className="flex items-center gap-1">
                    <Star size={10} className="fill-[#00e5ff] text-[#00e5ff]" />
                    <span className="text-[11px] text-white/55">{t.average_rating?.toFixed(1)} · {t.rating_count}</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ============= RIGHT: YouTube videos ============= */}
      <div className={widgetClass + " relative overflow-hidden p-0"}>
        <VideosRotator items={data.videos || []} loading={loading} navigate={navigate} />
      </div>
    </div>
  );
}

/* Marquee: neue Mitglieder — horizontaler Avatar-Scroll */
function MembersMarquee({ members, loading, onSeeAll }) {
  const items = (members || []).slice(0, 10);
  const doubled = [...items, ...items]; // Endlos-Loop
  return (
    <div className="relative border-b border-white/[0.06] bg-black/40">
      <div className="flex items-center justify-between px-4 pt-3 pb-1.5">
        <div className="flex items-center gap-2">
          <UsersIcon size={12} className="text-[#00e5ff]" />
          <span className="text-[9.5px] uppercase tracking-[0.3em] text-[#00e5ff]/80 font-outfit font-semibold">
            Neueste Mitglieder
          </span>
        </div>
        <button onClick={onSeeAll} className="text-white/30 hover:text-[#00e5ff] transition-colors" aria-label="Alle Mitglieder">
          <ChevronRight size={13} />
        </button>
      </div>
      <div className="relative overflow-hidden h-[72px]">
        {loading ? (
          <div className="flex gap-3 p-3">
            {Array.from({ length: 5 }).map((_, i) => <div key={`mm-skel-${i}`} className="w-12 h-12 rounded-full bg-white/[0.03] animate-pulse shrink-0" />)}
          </div>
        ) : items.length === 0 ? (
          <p className="text-[11px] text-white/30 px-4 pt-4">Noch keine anderen Mitglieder.</p>
        ) : (
          <>
            <div className="absolute inset-0 flex items-center gap-4 animate-[members-scroll_28s_linear_infinite] will-change-transform px-4">
              {doubled.map((m, idx) => (
                <div key={`${m.id}-${idx}`} className="shrink-0 flex items-center gap-2.5 group">
                  <div className="relative">
                    <Avatar src={m.avatar_url} name={`${m.first_name} ${m.last_name}`} size={40} className="ring-1 ring-white/10 group-hover:ring-[#00e5ff]/50 transition-all" />
                    {m.founding_member && (
                      <span
                        title={`Gründungsmitglied #${m.founding_number}`}
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center ring-2 ring-black"
                        data-testid={`board-founding-${m.id}`}
                      >
                        <Crown size={9} className="text-black" strokeWidth={2.8} />
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 pr-3">
                    <p className="text-[12px] font-outfit font-semibold text-white truncate leading-tight max-w-[140px]">
                      {m.first_name} {m.last_name}
                    </p>
                    <p className="text-[10px] text-white/40 truncate max-w-[140px] leading-tight mt-0.5">
                      {m.company || m.position || "Mitglied"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {/* Edge fades */}
            <div className="pointer-events-none absolute top-0 bottom-0 left-0 w-8 bg-gradient-to-r from-black to-transparent z-10" />
            <div className="pointer-events-none absolute top-0 bottom-0 right-0 w-8 bg-gradient-to-l from-black to-transparent z-10" />
          </>
        )}
      </div>
      <style>{`
        @keyframes members-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

/* Rotating YouTube videos widget — reuses look of NewsRotator */
function VideosRotator({ items, loading, navigate }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setI((v) => (v + 1) % items.length), 4200);
    return () => clearInterval(t);
  }, [items.length]);
  if (loading) return <div className="h-full min-h-[150px] animate-pulse bg-white/[0.02]" />;
  if (!items.length) {
    return (
      <div className="h-full flex flex-col p-5 justify-between min-h-[150px]">
        <div className="flex items-center gap-2">
          <PlayCircle size={14} className="text-[#00e5ff]" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#00e5ff]/80 font-outfit font-semibold">YouTube</span>
        </div>
        <p className="text-xs text-white/35">Noch keine Videos geladen</p>
      </div>
    );
  }
  const v = items[i];
  const thumb = v.image_url || v.fallback_image_url;
  return (
    <button
      onClick={() => navigate("/news/videos")}
      data-testid="board-widget-video"
      className="relative block w-full h-full min-h-[190px] group text-left"
    >
      {thumb && (
        <img
          src={thumb}
          alt={v.title}
          className="absolute inset-0 w-full h-full object-cover opacity-75 group-hover:opacity-90 transition-opacity"
          onError={(e) => { if (v.fallback_image_url) e.target.src = v.fallback_image_url; }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
      {/* Play overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-11 h-11 border border-white/30 bg-black/60 backdrop-blur-sm flex items-center justify-center group-hover:border-[#00e5ff] group-hover:bg-[#00e5ff]/10 transition-colors">
          <PlayCircle size={20} className="text-white group-hover:text-[#00e5ff] transition-colors" />
        </div>
      </div>
      <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
        <PlayCircle size={12} className="text-[#00e5ff]" />
        <span className="text-[10px] uppercase tracking-[0.28em] text-[#00e5ff] font-outfit font-semibold">YouTube</span>
      </div>
      <div className="absolute top-3 right-3 z-10">
        <ChevronRight size={14} className="text-white/60" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
        <div className="inline-block px-1.5 py-0 mb-1.5 text-[9px] uppercase tracking-[0.18em] font-outfit font-bold"
             style={{ background: `${v.color}22`, color: v.color, border: `1px solid ${v.color}60` }}>
          {v.channel_name}
        </div>
        <p className="text-[13px] font-outfit font-semibold text-white leading-snug line-clamp-2">{v.title}</p>
      </div>
      {/* Pagination dots */}
      {items.length > 1 && (
        <div className="absolute bottom-2 right-3 flex gap-1 z-10">
          {items.map((item, k) => (
            <span key={`dot-${item.video_id || item.url || k}`} className={`w-1 h-1 rounded-full transition-colors ${k === i ? "bg-[#00e5ff]" : "bg-white/25"}`} />
          ))}
        </div>
      )}
    </button>
  );
}


function NewsRotator({ items, loading, navigate }) {
  const [idx, setIdx] = useState(0);
  const list = (items || []).slice(0, 5);
  useEffect(() => {
    if (list.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % list.length), 3000);
    return () => clearInterval(id);
  }, [list.length]);

  if (loading) return <div className="p-5 h-[240px]"><div className="h-full animate-pulse bg-white/[0.02]" /></div>;
  if (list.length === 0) return (
    <div className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <Newspaper size={14} className="text-[#00e5ff]" />
        <span className="text-[10px] uppercase tracking-[0.28em] text-[#00e5ff]/80 font-outfit font-semibold">KI-News</span>
      </div>
      <p className="text-xs text-white/35">Noch keine News geladen</p>
    </div>
  );

  const n = list[idx];
  return (
    <button
      onClick={() => navigate(`/news?open=${encodeURIComponent(n.url)}`)}
      data-testid="news-widget-rotator"
      className="group/nr block text-left w-full h-full"
    >
      {/* Image with gradient overlay */}
      <div className="relative h-[200px] bg-black overflow-hidden">
        {n.image_url ? (
          <img
            key={n.url}
            src={n.image_url}
            alt=""
            className="w-full h-full object-cover opacity-70 group-hover/nr:opacity-90 transition-opacity duration-500 news-fade-in"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#00e5ff]/10 to-transparent" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] font-outfit font-bold bg-[#00e5ff] text-black">
            <Newspaper size={10} /> KI-News
          </span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-white/70 bg-black/60 px-2 py-0.5 border border-white/10 font-outfit">{n.source}</span>
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <p key={n.url + "-t"} className="text-[13px] font-outfit font-semibold text-white leading-snug line-clamp-3 news-fade-in">{n.title}</p>
        </div>
      </div>
      {/* Pagination dots */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-black">
        <div className="flex items-center gap-1.5">
          {list.map((it, i) => (
            <span key={it.url} className={`h-1 transition-all duration-300 ${i === idx ? "w-5 bg-[#00e5ff]" : "w-1 bg-white/20"}`} />
          ))}
        </div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 group-hover/nr:text-[#00e5ff] font-outfit transition-colors flex items-center gap-1">
          Lesen <ChevronRight size={11} />
        </span>
      </div>
      <style>{`
        .news-fade-in { animation: nf 0.5s ease-out; }
        @keyframes nf { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) { .news-fade-in { animation: none; } }
      `}</style>
    </button>
  );
}

/* ===================================================================== */
/* Post Card                                                              */
/* ===================================================================== */
function PostCard({ post, user, onDelete, onOpenDm, onOpenDetail }) {
  const cat = getCatStyle(post.category);
  const Icon = cat.icon;
  const remaining = daysRemaining(post.expires_at);
  const isOwner = (user?._id === post.author_id) || (user?.id === post.author_id);
  const canDelete = isOwner || user?.role === "admin";
  const urgent = remaining <= 2;
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const imgSrc = post.image_url ? (post.image_url.startsWith("/") ? `${apiBase}${post.image_url}` : post.image_url) : null;

  return (
    <div
      className="group relative bg-[#0c0c0c] border border-white/[0.07] hover:border-[#00e5ff]/40 transition-all duration-300 flex flex-col cursor-pointer"
      style={{ borderTopColor: cat.hex, borderTopWidth: 3 }}
      data-testid={`post-card-${post.id}`}
      onClick={() => onOpenDetail(post)}
    >
      {imgSrc && (
        <button
          onClick={() => onOpenDetail(post)}
          className="block w-full h-44 bg-black overflow-hidden"
          data-testid={`post-image-${post.id}`}
        >
          <img src={imgSrc} alt={post.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" loading="lazy" />
        </button>
      )}

      <div className="p-5 flex-1 flex flex-col">
        {/* Category badge */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-outfit font-semibold"
               style={{ color: cat.hex, background: `${cat.hex}12`, border: `1px solid ${cat.hex}40` }}>
            <Icon size={11} />
            {cat.label}
          </div>
          <div className={`flex items-center gap-1 text-[10px] uppercase tracking-wider font-outfit ${urgent ? "text-red-400" : "text-white/35"}`}>
            <Clock size={11} />
            {remaining === 0 ? "heute" : `${remaining}T`}
          </div>
        </div>

        <h3 className="font-outfit font-bold text-white text-[16px] leading-snug mb-2 line-clamp-2">{post.title}</h3>
        <p className="text-[13px] text-white/65 leading-relaxed line-clamp-5 mb-4 flex-1"><Linkify text={post.description} /></p>

        {post.tool_ref && (
          <a
            href={post.tool_ref.website_url || "#"}
            target="_blank" rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid={`post-tool-ref-${post.id}`}
            className="mb-3 flex items-center gap-2 border border-[#00e5ff]/30 bg-[#00e5ff]/[0.04] hover:bg-[#00e5ff]/10 transition-colors px-2.5 py-2"
          >
            {post.tool_ref.logo_url && <img src={post.tool_ref.logo_url} alt="" className="w-6 h-6 object-contain bg-white/5" />}
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wider text-[#00e5ff]/80 font-outfit font-bold">KI-Tool-Empfehlung</p>
              <p className="text-[12px] text-white truncate font-outfit font-semibold">{post.tool_ref.name}</p>
            </div>
          </a>
        )}

        {post.budget_option && (
          <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-white/40 font-outfit">
            Budget: <span className="text-white/70">{BUDGET_OPTIONS.find((b) => b.key === post.budget_option)?.label || post.budget_option}</span>
          </div>
        )}

        {/* Footer: author + actions */}
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-white/[0.06]">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Avatar src={post.author_avatar_url} name={post.author_name} size={28} />
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-outfit font-semibold text-white truncate">{post.author_name || "Unbekannt"}</p>
              <p className="text-[10px] text-white/35">{formatDate(post.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {!isOwner && (
              <button
                onClick={(e) => { e.stopPropagation(); onOpenDm(post.author_id, post.author_name); }}
                data-testid={`post-dm-${post.id}`}
                className="p-2 text-white/40 hover:text-[#00e5ff] hover:bg-[#00e5ff]/5 transition-colors"
                title="Autor anschreiben"
                aria-label="Autor anschreiben"
              >
                <MessageCircle size={14} />
              </button>
            )}
            {canDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(post.id); }}
                data-testid={`post-delete-${post.id}`}
                className="p-2 text-white/40 hover:text-red-400 hover:bg-red-400/5 transition-colors"
                title="Löschen"
                aria-label="Löschen"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===================================================================== */
/* Create Dialog                                                          */
/* ===================================================================== */
function CreatePostDialog({ open, onOpenChange, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Frage");
  const [budget, setBudget] = useState("kein-budget");
  const [imageUrl, setImageUrl] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const reset = () => {
    setTitle(""); setDescription(""); setCategory("Frage"); setBudget("kein-budget");
    setImageUrl(""); setImagePreview(""); setError("");
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      setError("Bild zu groß (max 20 MB)");
      return;
    }
    setUploading(true); setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/uploads/board-image`, form, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      setImageUrl(data.image_url);
      setImagePreview(`${process.env.REACT_APP_BACKEND_URL}${data.image_url}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/posts`, {
        title, description, category, budget_option: budget, image_url: imageUrl || null,
      }, { withCredentials: true });
      onCreated?.(data);
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erstellen fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white rounded-none max-w-lg p-0 max-h-[92vh] overflow-y-auto" data-testid="create-post-dialog">
        <DialogHeader className="p-5 border-b border-white/10">
          <DialogTitle className="font-outfit font-bold text-xl text-white">Neuer Post-It</DialogTitle>
          <DialogDescription className="text-[13px] text-white/50">
            Dein Beitrag ist 10 Tage lang sichtbar und öffentlich für alle Mitglieder.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 border border-red-400/30 px-3 py-2" data-testid="create-error">
              <AlertCircle size={14} />{error}
            </div>
          )}

          <div>
            <Label className="text-white/50 text-[10px] uppercase tracking-[0.22em] font-outfit font-semibold">Kategorie *</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const active = category === c.key;
                return (
                  <button
                    type="button"
                    key={c.key}
                    onClick={() => setCategory(c.key)}
                    data-testid={`cat-select-${c.key}`}
                    className={`flex items-center gap-2 px-3 py-2.5 text-[13px] font-outfit font-medium transition-all border ${active ? "" : "border-white/10 text-white/55 hover:text-white hover:border-white/25 bg-transparent"}`}
                    style={active ? { color: c.hex, background: `${c.hex}14`, borderColor: `${c.hex}80` } : {}}
                  >
                    <Icon size={14} />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-white/50 text-[10px] uppercase tracking-[0.22em] font-outfit font-semibold">Titel *</Label>
            <Input required maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid="create-title" placeholder="Kurz und prägnant (max. 80 Zeichen)"
              className="mt-1.5 bg-black border-white/10 text-white rounded-none h-10 focus:border-[#00e5ff]/60 focus:ring-0" />
          </div>

          <div>
            <Label className="text-white/50 text-[10px] uppercase tracking-[0.22em] font-outfit font-semibold">Beschreibung *</Label>
            <Textarea required maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)}
              data-testid="create-description" placeholder="Was genau brauchst du / bietest du / willst du teilen? (max. 500 Zeichen)"
              className="mt-1.5 bg-black border-white/10 text-white rounded-none focus:border-[#00e5ff]/60 focus:ring-0 min-h-[100px] text-sm" />
            <p className="text-[11px] text-white/30 mt-1 text-right">{description.length}/500</p>
          </div>

          <div>
            <Label className="text-white/50 text-[10px] uppercase tracking-[0.22em] font-outfit font-semibold">Budget</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {BUDGET_OPTIONS.map((b) => {
                const active = budget === b.key;
                return (
                  <button
                    type="button"
                    key={b.key}
                    onClick={() => setBudget(b.key)}
                    data-testid={`budget-${b.key}`}
                    className={`px-3 py-2 text-[12px] font-outfit transition-colors border ${active ? "bg-[#00e5ff]/10 text-[#00e5ff] border-[#00e5ff]/50" : "border-white/10 text-white/55 hover:text-white hover:border-white/25"}`}
                  >
                    {b.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-white/50 text-[10px] uppercase tracking-[0.22em] font-outfit font-semibold">Bild anhängen (optional)</Label>
            <div className="mt-2">
              {imagePreview ? (
                <div className="relative border border-white/10">
                  <img src={imagePreview} alt="Vorschau" className="w-full max-h-56 object-cover" />
                  <button
                    type="button"
                    onClick={() => { setImageUrl(""); setImagePreview(""); }}
                    data-testid="create-remove-image"
                    className="absolute top-2 right-2 p-1.5 bg-black/70 hover:bg-red-500 text-white transition-colors"
                    aria-label="Bild entfernen"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="create-attach-image"
                  className="w-full flex items-center justify-center gap-2 px-4 py-6 border border-dashed border-white/15 hover:border-[#00e5ff]/50 hover:bg-[#00e5ff]/[0.03] text-white/55 hover:text-[#00e5ff] transition-colors text-sm font-outfit"
                >
                  {uploading ? (
                    <><div className="w-3 h-3 border-2 border-[#00e5ff] border-t-transparent rounded-full animate-spin" /> Lädt hoch…</>
                  ) : (
                    <><Paperclip size={14} /> Foto anhängen (JPG, PNG, WebP · max 20 MB)</>
                  )}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/bmp"
                onChange={onFile}
                className="hidden"
                data-testid="create-image-input"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-2">
            <div className="border-l-4 border-red-500/80 bg-red-500/10 p-3" data-testid="post-public-warning">
              <p className="text-[13px] font-outfit font-bold text-red-400 leading-tight">Achtung!</p>
              <p className="text-[12.5px] text-red-200/80 leading-snug mt-0.5">
                Dieser Post-It ist für <b>alle Mitglieder in diesem Forum</b> öffentlich einsehbar.
              </p>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={submitting || uploading || !title || !description}
                data-testid="create-submit"
                className="flex-1 bg-[#00e5ff] text-black hover:bg-[#00e5ff] rounded-none font-outfit font-semibold h-11 text-sm">
                {submitting ? "…" : "Post-It erstellen"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}
                className="border border-white/15 text-white/70 hover:text-white rounded-none h-11 px-4 text-sm">
                Abbrechen
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ===================================================================== */
/* Detail Dialog                                                          */
/* ===================================================================== */
function DetailDialog({ post, open, onOpenChange, onOpenDm, canDelete, onDelete, onEdit }) {
  if (!post) return null;
  const cat = getCatStyle(post.category);
  const Icon = cat.icon;
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const imgSrc = post.image_url ? (post.image_url.startsWith("/") ? `${apiBase}${post.image_url}` : post.image_url) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white rounded-none max-w-2xl p-0 max-h-[92vh] overflow-y-auto" data-testid="detail-dialog">
        {imgSrc && <img src={imgSrc} alt={post.title} className="w-full max-h-80 object-cover" />}
        <div className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-outfit font-semibold"
                 style={{ color: cat.hex, background: `${cat.hex}12`, border: `1px solid ${cat.hex}40` }}>
              <Icon size={11} />{cat.label}
            </div>
            <span className="text-[11px] text-white/40">{formatDate(post.created_at)} · noch {daysRemaining(post.expires_at)} Tage</span>
          </div>
          <DialogTitle className="font-outfit font-bold text-2xl text-white mb-4">{post.title}</DialogTitle>
          <p className="text-[15px] text-white/80 leading-relaxed whitespace-pre-wrap mb-6"><Linkify text={post.description} /></p>
          {post.budget_option && (
            <p className="text-sm text-white/50 mb-6">
              <span className="uppercase tracking-[0.18em] text-[11px] text-[#00e5ff]/70 mr-2">Budget</span>
              {BUDGET_OPTIONS.find((b) => b.key === post.budget_option)?.label || post.budget_option}
            </p>
          )}
          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            <div className="flex items-center gap-3">
              <Avatar src={post.author_avatar_url} name={post.author_name} size={36} />
              <div>
                <p className="font-outfit font-semibold text-white text-sm">{post.author_name}</p>
                <p className="text-[11px] text-white/40">Autor</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => { onOpenDm(post.author_id, post.author_name); onOpenChange(false); }}
                data-testid="detail-dm-btn"
                className="bg-[#00e5ff] text-black hover:bg-[#00e5ff] rounded-none h-10 px-4 font-outfit font-semibold text-sm"
              >
                <MessageCircle size={14} className="mr-2" /> DM senden
              </Button>
              {canDelete && onEdit && (
                <Button onClick={() => { onEdit(post); onOpenChange(false); }} variant="ghost"
                  data-testid="detail-edit-btn"
                  className="border border-white/20 hover:border-[#00e5ff] hover:text-[#00e5ff] text-white/70 rounded-none h-10 px-3">
                  <Pencil size={14} />
                </Button>
              )}
              {canDelete && (
                <Button onClick={() => { onDelete(post.id); onOpenChange(false); }} variant="ghost"
                  data-testid="detail-delete-btn"
                  className="border border-red-400/30 hover:border-red-400 hover:text-red-400 text-red-400/80 rounded-none h-10 px-3">
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ===================================================================== */
/* Page                                                                   */
/* ===================================================================== */
export default function BoardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("Alle");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailPost, setDetailPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [postCount, setPostCount] = useState({ count: 0, limit: 10, remaining: 10 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/posts`, {
        params: { category: category === "Alle" ? undefined : category },
        withCredentials: true,
      });
      setPosts(data);
    } catch (err) {
      console.error("[board] load posts failed:", err?.response?.status, err?.message);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [category]);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/posts/my/count`, { withCredentials: true });
      setPostCount(data);
    } catch (err) {
      console.debug("[board] loadCount failed:", err?.response?.status);
    }
  }, []);

  useEffect(() => { load(); loadCount(); }, [load, loadCount]);

  const handleCreated = (post) => { setPosts((p) => [post, ...p]); loadCount(); };

  const [editPost, setEditPost] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const handleEdit = (post) => {
    setEditTitle(post.title);
    setEditDescription(post.description);
    setEditPost(post);
  };

  const handleEditSave = async () => {
    if (!editPost) return;
    setEditSaving(true);
    try {
      await axios.put(API + "/posts/" + editPost.id, {
        title: editTitle,
        description: editDescription,
        category: editPost.category,
        budget_option: editPost.budget_option || "kein-budget",
        // Preserve image + tool-recommendation across edits — the dialog
        // doesn't surface them, and the backend used to wipe them on save.
        image_url: editPost.image_url || "",
        tool_ref: editPost.tool_ref || null,
      }, { withCredentials: true });
      loadPosts();
      setEditPost(null);
      setDetailPost(null);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = status === 401
        ? "Deine Sitzung ist abgelaufen. Bitte neu anmelden."
        : `Speichern fehlgeschlagen${status ? ` (HTTP ${status})` : ""}${detail ? ": " + detail : ""}`;
      alert(msg);
    }
    finally { setEditSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Post-It wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/posts/${id}`, { withCredentials: true });
      setPosts((p) => p.filter((x) => x.id !== id));
      loadCount();
    } catch (err) {
      alert(err?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const openDm = (userId, userName) => {
    navigate(`/chat?dm=${userId}&name=${encodeURIComponent(userName || "")}`);
  };

  const counts = useMemo(() => {
    const c = { Alle: posts.length };
    CATEGORIES.forEach((cat) => { c[cat.key] = posts.filter((p) => p.category === cat.key).length; });
    return c;
  }, [posts]);

  const canDeletePost = (post) =>
    (user?._id === post.author_id) || (user?.id === post.author_id) || user?.role === "admin";

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-16">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-px bg-[#00e5ff]" />
              <span className="font-outfit text-[11px] uppercase tracking-[0.32em] text-[#00e5ff]/80 font-semibold">
                Schwarzes Brett
              </span>
            </div>
            <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95] text-white" data-testid="board-heading">
              {(() => {
                const h = new Date().getHours();
                const name = user?.first_name || "";
                if (h < 12) return <>Guten Morgen{name ? <>, <span className="text-[#00e5ff]">{name}</span></> : <> <span className="text-[#00e5ff]">Board</span></>}</>;
                if (h < 14) return <>Mahlzeit{name ? <>, <span className="text-[#00e5ff]">{name}</span></> : <> <span className="text-[#00e5ff]">Board</span></>}</>;
                if (h < 17) return <>Hi{name ? <>, <span className="text-[#00e5ff]">{name}</span></> : <> <span className="text-[#00e5ff]">Board</span></>}</>;
                return <>Guten Abend{name ? <>, <span className="text-[#00e5ff]">{name}</span></> : <> <span className="text-[#00e5ff]">Board</span></>}</>;
              })()}
            </h1>
            <p className="text-base text-white/55 mt-2">Frag, hilf, teile. Einträge sind 10 Tage sichtbar.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-outfit text-right">
              <div>Dein Monat</div>
              <div className="text-white text-lg font-bold tracking-tight" data-testid="board-quota">
                {postCount.count}<span className="text-white/30">/{postCount.limit}</span>
              </div>
            </div>
            <Button
              onClick={() => setCreateOpen(true)}
              disabled={postCount.remaining <= 0}
              data-testid="create-post-btn"
              className="bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none font-outfit font-semibold h-11 px-5 text-sm"
            >
              <Plus size={16} className="mr-2" /> Neuer Post-It
            </Button>
          </div>
        </div>

        {/* Widget row */}
        <WidgetRow />

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="board-filters">
          <button
            onClick={() => setCategory("Alle")}
            data-testid="filter-Alle"
            className={`px-3.5 py-2 text-[13px] font-outfit tracking-wide transition-all border ${category === "Alle" ? "bg-[#00e5ff] text-black border-[#00e5ff] font-semibold" : "border-white/10 text-white/60 hover:text-white hover:border-white/25"}`}
          >
            <Layers size={13} className="inline mr-2" /> Alle <span className="text-[11px] opacity-60">({counts.Alle})</span>
          </button>
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = category === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                data-testid={`filter-${c.key.replace(" ", "-")}`}
                className={`px-3.5 py-2 text-[13px] font-outfit tracking-wide transition-all border ${active ? "font-semibold" : "border-white/10 text-white/60 hover:text-white hover:border-white/25"}`}
                style={active ? { color: c.hex, background: `${c.hex}14`, borderColor: `${c.hex}80` } : {}}
              >
                <Icon size={13} className="inline mr-2" />
                {c.label} <span className="text-[11px] opacity-60">({counts[c.key] || 0})</span>
              </button>
            );
          })}
        </div>

        {/* Posts grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => <div key={`skel-board-${i}`} className="h-56 border border-white/[0.05] bg-white/[0.015] animate-pulse" />)}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <ImageIcon size={36} className="mx-auto mb-3 text-white/20" />
            <p className="font-outfit text-white/45">Noch keine Einträge in dieser Kategorie. Sei der Erste!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="board-grid">
            {posts.map((p) => (
              <PostCard
                key={p.id}
                post={p}
                user={user}
                onDelete={handleDelete}
                onOpenDm={openDm}
                onOpenDetail={setDetailPost}
              />
            ))}
          </div>
        )}
      </main>

      <CreatePostDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={handleCreated} />
      <DetailDialog
        post={detailPost}
        open={!!detailPost}
        onOpenChange={(o) => !o && setDetailPost(null)}
        onOpenDm={openDm}
        canDelete={detailPost ? canDeletePost(detailPost) : false}
        onDelete={handleDelete}
        onEdit={handleEdit}
      />

      {editPost && (
        <Dialog open={!!editPost} onOpenChange={(v) => { if (!v) setEditPost(null); }}>
          <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white rounded-none max-w-lg p-6">
            <DialogTitle className="font-outfit font-bold text-xl text-white mb-4">Post bearbeiten</DialogTitle>
            <div className="space-y-4">
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="Titel"
                className="bg-white/[0.04] border-white/10 text-white rounded-none h-11" maxLength={80} />
              <Textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} placeholder="Beschreibung"
                className="bg-white/[0.04] border-white/10 text-white rounded-none min-h-[120px]" maxLength={500} />
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => setEditPost(null)}
                  className="border border-white/15 text-white/70 rounded-none h-10 px-4">Abbrechen</Button>
                <Button onClick={handleEditSave} disabled={editSaving || !editTitle.trim() || !editDescription.trim()}
                  className="bg-[#00e5ff] text-black hover:bg-[#00e5ff] rounded-none h-10 px-6 font-outfit font-semibold">
                  {editSaving ? "..." : "Speichern"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
